import { FeatureCode } from 'generated/graphql';
import { isEmpty } from 'lodash';
import { z } from 'zod';

export const EmailCaptureSection = z
  .object({
    type: z.literal('EmailCapture'),
    enabled: z.boolean(),
    bgColor: z.string(),
    colorIndex: z.number().optional(),
    title: z.string().optional().default(''),
    description: z.string().optional().default(''),
    displayAsPopup: z.boolean().optional().nullable(),
    image: z.string().optional().nullable(),
  })
  .superRefine((data, ctx) => {
    // Only validate required fields when section is enabled
    if (data.enabled) {
      if (!data.title || data.title.length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.too_small,
          minimum: 1,
          type: 'string',
          inclusive: true,
          message: 'Title is required',
          path: ['title'],
        });
      } else if (data.title.length > 60) {
        ctx.addIssue({
          code: z.ZodIssueCode.too_big,
          maximum: 60,
          type: 'string',
          inclusive: true,
          message: 'Title is too long',
          path: ['title'],
        });
      }

      if (!data.description || data.description.length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.too_small,
          minimum: 1,
          type: 'string',
          inclusive: true,
          message: 'Subtitle is required',
          path: ['description'],
        });
      } else if (data.description.length > 240) {
        ctx.addIssue({
          code: z.ZodIssueCode.too_big,
          maximum: 240,
          type: 'string',
          inclusive: true,
          message: 'Subtitle is too long',
          path: ['description'],
        });
      }
    }
  });

const Bullets = z
  .array(
    z.object({
      text: z.string().min(1, "Can't be empty"),
    }),
  )
  .optional()
  .default([]);

const commonFields = {
  enabled: z.boolean(),
  bgColor: z.string(),
  colorIndex: z.number().optional(),
};

export const IntroductionSection = z.object({
  ...commonFields,
  type: z.literal('Introduction'),
  enabled: z.literal(true),
  title: z.string().min(1, { message: 'Title is required' }).max(60, {
    message: 'Title is too long',
  }),
  subtitle: z.string().min(1, { message: 'Subtitle is required' }).max(60, {
    message: 'Subtitle is too long',
  }),
  bullets: Bullets,
  coverImage: z.string().min(1, { message: 'Cover image is required' }),
  introVideo: z.string().nullable(),
});

export const HeroFullVideoSection = z.object({
  ...commonFields,
  type: z.literal('HeroFullVideo'),
  enabled: z.literal(true),
  title: z.string().max(60, {
    message: 'Title is too long',
  }),
  bullets: Bullets,
  introVideo: z.string().min(1, { message: 'Video is required' }),
});

export const HeroFullImageSection = z.object({
  ...commonFields,
  type: z.literal('HeroFullImage'),
  enabled: z.literal(true),
  title: z.string().max(60, {
    message: 'Title is too long',
  }),
  bullets: Bullets,
  coverImage: z.string().min(1, { message: 'Image is required' }),
});

export const LogosSection = z.object({
  ...commonFields,
  type: z.literal('Logos'),
  logos: z
    .array(
      z.object({
        url: z.string().min(1, { message: 'Logo URL is required' }),
      }),
    )
    .min(3, { message: 'At least 3 logos are required' }),
});

export const ReviewsSections = z.object({
  ...commonFields,
  type: z.literal('Reviews'),
  reviews: z
    .array(
      z.object({
        name: z.string().min(1, { message: 'Name is required' }),
        text: z.string().min(1, { message: 'Text is required' }),
      }),
    )
    .optional()
    .default([]),
});

export const TextSection2 = z.object({
  ...commonFields,
  type: z.literal('TextSection2'),
  title: z.string().min(1, { message: 'Title is required' }),
  desc: z.string().optional(),
  bullets: Bullets,
  images: z
    .array(z.object({ url: z.string().nullable().default(null) }))
    .optional()
    .default([]),
  rotateImages: z.boolean().optional(),
  swapSides: z.boolean().optional(),
});

export const TextSection1 = z.object({
  ...commonFields,
  type: z.literal('TextSection1'),
  title: z.string().min(1, { message: 'Title is required' }),
  desc: z.string().optional(),
  bullets: Bullets,
  coverImage: z.string().min(1, { message: 'Cover image is required' }),
  introVideo: z.string().nullable(),
  swapSides: z.boolean().optional(),
});

export const TextSection = z.object({
  ...commonFields,
  type: z.literal('TextSection'),
  title: z.string().min(1, { message: 'Title is required' }),
  desc: z.string().optional(),
});

export const TestimonialsSection = z.object({
  ...commonFields,
  type: z.literal('Testimonials'),
  title: z.string().optional().default(''),
  testimonials: z
    .array(
      z.object({
        video: z.string().nullable().default(null),
        image: z.string().nullable().default(null),
        name: z.string().optional(),
        text: z.string().optional(),
      }),
    )
    .optional()
    .default([]),
});

export const FaqsSection = z.object({
  ...commonFields,
  type: z.literal('FAQs'),
  title: z.string(),
  faqs: z
    .array(
      z.object({
        question: z.string().min(1, { message: 'Question is required' }),
        answer: z.string().min(1, { message: 'Answer is required' }),
      }),
    )
    .optional()
    .default([]),
});

export const FeaturedBenefit = z
  .object({
    ...commonFields,
    type: z.literal('FeaturedBenefit'),
    title: z.string().optional().default(''),
    bullets: Bullets,
    coverImage: z.string().optional().default(''),
    swapSides: z.boolean().optional(),
  })
  .superRefine((data, ctx) => {
    // Only validate required fields when section is enabled
    if (data.enabled) {
      if (!data.title || data.title.length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.too_small,
          minimum: 1,
          type: 'string',
          inclusive: true,
          message: 'Title is required',
          path: ['title'],
        });
      }
      if (!data.coverImage || data.coverImage.length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.too_small,
          minimum: 1,
          type: 'string',
          inclusive: true,
          message: 'Cover image is required',
          path: ['coverImage'],
        });
      }
    }
  });

export const ProgramsHighlight = z
  .object({
    ...commonFields,
    type: z.literal('ProgramsHighlight'),
    title: z.string().optional().default(''),
    bullets: Bullets,
    swapSides: z.boolean().optional(),
    programIds: z.array(z.number()).optional().default([]),
  })
  .superRefine((data, ctx) => {
    // Only validate required fields when section is enabled
    if (data.enabled) {
      if (!data.title || data.title.length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.too_small,
          minimum: 1,
          type: 'string',
          inclusive: true,
          message: 'Title is required',
          path: ['title'],
        });
      }
      if (!data.programIds || data.programIds.length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.too_small,
          minimum: 1,
          type: 'array',
          inclusive: true,
          message: 'Select program to highlight',
          path: ['programIds'],
        });
      }
    }
  });

export const ProgramsList = z.object({
  ...commonFields,
  type: z.literal('ProgramsList'),
  title: z.string().min(1, { message: 'Title is required' }),
  subtitle: z.string(),
  programIds: z.array(z.number()).optional(),
});

export const PaidProgramsList = ProgramsList.extend({
  type: z.literal('PaidProgramsList'),
});

export const FeaturesSection = z.object({
  ...commonFields,
  type: z.literal('Features'),
  title: z.string().optional(),
  remindersCoverImage: z.string().nullable().default(null),
  streaksCoverImage: z.string().nullable().default(null),
  workoutsCoverImage: z.string().nullable().default(null),
  favoritesCoverImage: z.string().nullable().default(null),
  lifetimeCoverImage: z.string().nullable().default(null),
  streamCoverImage: z.string().nullable().default(null),
});

export const SubscriptionCarouselSection = z.object({
  type: z.literal('SubscriptionCarousel'),
  enabled: z.boolean(),
  bgColor: z.string(),
  colorIndex: z.number().optional(),
  offerId: z.number(),
  title: z.string().min(1, 'Headline text is required'),
  subtitle: z.string().optional().default(''),
  programIds: z.array(z.number()).optional().default([]),
  extrasIds: z.array(z.string()).optional().default([]),
  communityFeatures: z.array(z.nativeEnum(FeatureCode)).optional().default([]),
});

const FeatureType = z.union([
  z.literal('reminders'),
  z.literal('streaks'),
  z.literal('tracking'),
  z.literal('community'),
  z.literal('favorites'),
  z.literal('videos'),
  z.literal('notes'),
  z.literal('stream'),
  z.literal('offline'),
  z.literal('playlist'),
  z.literal('nutrition'),
  z.literal('messages'),
]);

export const FeaturesNewSection = z.object({
  ...commonFields,
  type: z.literal('FeaturesNew'),
  title: z.string().optional(),
  features: z
    .array(
      z.object({
        type: FeatureType,
        name: z.string(),
        image: z.string(),
      }),
    )
    .length(6, { message: 'Select 6 features' }),
});

export const BioSection = z
  .object({
    ...commonFields,
    type: z.literal('Bio'),
    title: z.string().optional().default(''),
    desc: z.string().optional().default(''),
    coverImage: z.string().optional().default(''),
    introVideo: z.string().nullable(),
    swapSides: z.boolean().optional(),
  })
  .superRefine((data, ctx) => {
    // Only validate required fields when section is enabled
    if (data.enabled) {
      if (!data.title || data.title.length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.too_small,
          minimum: 1,
          type: 'string',
          inclusive: true,
          message: 'Title is required',
          path: ['title'],
        });
      }
      if (!data.desc || data.desc.length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.too_small,
          minimum: 1,
          type: 'string',
          inclusive: true,
          message: 'Description is required',
          path: ['desc'],
        });
      }
      if (!data.coverImage || data.coverImage.length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.too_small,
          minimum: 1,
          type: 'string',
          inclusive: true,
          message: 'Cover image is required',
          path: ['coverImage'],
        });
      }
    }
  });

const Branding = z.object({
  logo: z.string().nullable().default(null),
  primaryColor: z.string(),
});

type AllSectionUnion =
  | typeof IntroductionSection
  | typeof EmailCaptureSection
  | typeof HeroFullVideoSection
  | typeof HeroFullImageSection
  | typeof LogosSection
  | typeof PaidProgramsList
  | typeof ProgramsList
  | typeof BioSection
  | typeof ReviewsSections
  | typeof TestimonialsSection
  | typeof FeaturesSection
  | typeof FeaturesNewSection
  | typeof FaqsSection
  | typeof ProgramsHighlight
  | typeof FeaturedBenefit
  | typeof TextSection
  | typeof TextSection1
  | typeof TextSection2
  | typeof SubscriptionCarouselSection;

const allowDisabled = <S extends AllSectionUnion>(schema: S) => {
  // Extract the type literal - handle both ZodObject and ZodEffects (from superRefine)
  const typeSchema =
    'shape' in schema
      ? schema.shape.type
      : '_def' in schema && 'schema' in schema._def
      ? (schema._def.schema as any).shape.type
      : z.string(); // fallback

  return z.union([
    schema,
    z.object({ enabled: z.literal(false), type: typeSchema }).passthrough(),
  ]);
};

export const LandingPagesBuilderSchemaV2 = z.object({
  branding: Branding,
  sections: z.array(
    z.union([
      IntroductionSection,
      HeroFullVideoSection,
      HeroFullImageSection,
      allowDisabled(EmailCaptureSection),
      allowDisabled(LogosSection),
      allowDisabled(TextSection),
      allowDisabled(TextSection1),
      allowDisabled(TextSection2),
      allowDisabled(PaidProgramsList),
      allowDisabled(ProgramsList),
      allowDisabled(SubscriptionCarouselSection),
      allowDisabled(BioSection),
      allowDisabled(ReviewsSections),
      allowDisabled(TestimonialsSection),
      allowDisabled(FeaturesSection),
      allowDisabled(FeaturesNewSection),
      allowDisabled(FaqsSection),
      allowDisabled(ProgramsHighlight),
      allowDisabled(FeaturedBenefit),
    ]),
  ),
});

export type ILandingPagesBuilderSchema = z.infer<
  typeof LandingPagesBuilderSchemaV2
>;

export type IIntroductionSection = z.infer<typeof IntroductionSection>;

export type IEmailCaptureSection = z.infer<typeof EmailCaptureSection>;

export type IHeroFullVideoSection = z.infer<typeof HeroFullVideoSection>;

export type IHeroFullImageSection = z.infer<typeof HeroFullImageSection>;

export type ILogosSection = z.infer<typeof LogosSection>;

export type ITextSection = z.infer<typeof TextSection>;

export type ITextSection1 = z.infer<typeof TextSection1>;

export type ITextSection2 = z.infer<typeof TextSection2>;

export type IFeaturedBenefit = z.infer<typeof FeaturedBenefit>;

export type IProgramsHighlight = z.infer<typeof ProgramsHighlight>;

export type IProgramsCarousel = z.infer<typeof ProgramsList>;

export type IPaidProgramsCarousel = z.infer<typeof PaidProgramsList>;

export type IBioSection = z.infer<typeof BioSection>;

export type IReviewsSection = z.infer<typeof ReviewsSections>;

export type ITestimonialsSection = z.infer<typeof TestimonialsSection>;

export type IFaqsSection = z.infer<typeof FaqsSection>;

export type IFeaturesSection = z.infer<typeof FeaturesSection>;

export type IFeaturesNewSection = z.infer<typeof FeaturesNewSection>;

export type ISubscriptionCarouselSection = z.infer<
  typeof SubscriptionCarouselSection
>;

export type ISection =
  | IIntroductionSection
  | IEmailCaptureSection
  | IHeroFullVideoSection
  | IHeroFullImageSection
  | ILogosSection
  | IBioSection
  | IReviewsSection
  | ITestimonialsSection
  | IFaqsSection
  | IFeaturesSection
  | IFeaturesNewSection
  | IProgramsCarousel
  | IPaidProgramsCarousel
  | IFeaturedBenefit
  | IProgramsHighlight
  | ITextSection
  | ITextSection1
  | ITextSection2
  | ISubscriptionCarouselSection;
